import type { AnchorHTMLAttributes, FormEvent, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useChatHistory } from '../../hooks/useChatHistory'
import { MaruyaApiError } from '../../lib/maruya-api'
import { formatThreadDate } from '../../lib/chat-history'
import {
  displayDocumentTitle,
  extractReferenceUrl,
  sourceTypeLabel,
  sourceUriLabel,
} from '../../lib/document-display'
import { buildAskHistory } from '../../lib/ask-history'
import {
  ASK_TIMEOUT_MS,
  FEEDBACK_COMMENT_MAX_LENGTH,
  askQuestion,
  isAbortError,
  submitAnswerFeedback,
} from '../../lib/rag-api'
import type { AnswerFeedbackRating, AskHistoryTurn, RagAskResponse } from '../../types/api'
import {
  IconChat,
  IconCheck,
  IconCopy,
  IconLink,
  IconPencil,
  IconPlus,
  IconSend,
  IconThumbsDown,
  IconThumbsUp,
  IconTrash,
  IconX,
} from '../../components/ui/Icon'
import { usePageHeader } from '../../components/layout/PageHeaderContext'
import { usePermissions } from '../../auth/usePermissions'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import {
  DeleteRequestDialog,
  type DeleteRequestDocument,
} from '../../components/rag/DeleteRequestDialog'

type DeleteTarget = {
  id: string
  title: string
}

type RenameTarget = {
  id: string
  value: string
}

type RetryState = {
  threadId: string
  question: string
  history: AskHistoryTurn[]
}

/** CX13: メッセージ単位の回答フィードバック送信状態 */
type FeedbackEntryState = {
  rating: AnswerFeedbackRating | null
  status: 'idle' | 'commenting' | 'submitting' | 'submitted' | 'error'
  comment: string
}

const IDLE_FEEDBACK_STATE: FeedbackEntryState = { rating: null, status: 'idle', comment: '' }

function MarkdownLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a {...props} target="_blank" rel="noopener noreferrer" />
}

const markdownComponents = { a: MarkdownLink }

const CITATIONS_COLLAPSED_LIMIT = 3

export function ChatPage() {
  const { canCurate } = usePermissions()
  const {
    ready,
    threads,
    activeThreadId,
    messages,
    searchQuery,
    setSearchQuery,
    filteredThreads,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
    appendUserMessage,
    appendAssistantMessage,
    editUserMessage,
  } = useChatHistory()
  const [question, setQuestion] = useState('')
  const [loading, setLoading] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canRetry, setCanRetry] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [feedbackState, setFeedbackState] = useState<Record<string, FeedbackEntryState>>({})
  const [visibleCitations, setVisibleCitations] = useState<Set<string>>(new Set())
  const [expandedCitations, setExpandedCitations] = useState<Set<string>>(new Set())
  const [knowledgeDeleteTarget, setKnowledgeDeleteTarget] =
    useState<DeleteRequestDocument | null>(null)
  const retryRef = useRef<RetryState | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const questionInputRef = useRef<HTMLTextAreaElement | null>(null)
  const copyFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  /** CX1: scrollHeight ベースで伸縮。CSS max-height (8rem) 超過分は内部スクロール */
  function resizeQuestionInput() {
    const el = questionInputRef.current
    if (!el) {
      return
    }
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useEffect(() => {
    resizeQuestionInput()
  }, [question, activeThreadId])

  useEffect(() => {
    setVisibleCitations(new Set())
    setExpandedCitations(new Set())
  }, [activeThreadId])

  function toggleCitationsVisible(messageId: string) {
    setVisibleCitations((current) => {
      const next = new Set(current)
      if (next.has(messageId)) {
        next.delete(messageId)
      } else {
        next.add(messageId)
      }
      return next
    })
  }

  function toggleCitationsExpanded(messageId: string) {
    setExpandedCitations((current) => {
      const next = new Set(current)
      if (next.has(messageId)) {
        next.delete(messageId)
      } else {
        next.add(messageId)
      }
      return next
    })
  }

  async function runAsk(
    threadId: string,
    trimmed: string,
    history: AskHistoryTurn[],
  ) {
    setLoading(true)
    setError(null)
    setCanRetry(false)
    retryRef.current = null
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const response = await askQuestion(trimmed, history, {
        signal: controller.signal,
      })
      appendAssistantMessage(threadId, response.answer, response)
    } catch (err) {
      if (abortControllerRef.current?.signal.aborted && isAbortError(err)) {
        return
      }
      const message =
        err instanceof MaruyaApiError
          ? (err.body.message ?? err.message)
          : err instanceof Error
            ? err.message
            : '質問の送信に失敗しました'
      setError(message)
      const retryable =
        err instanceof MaruyaApiError &&
        (err.body.error === 'ask_timeout' || err.status >= 500 || err.status === 408)
      if (retryable) {
        retryRef.current = { threadId, question: trimmed, history }
        setCanRetry(true)
      }
    } finally {
      abortControllerRef.current = null
      setLoading(false)
    }
  }

  function handleCancel() {
    abortControllerRef.current?.abort()
  }

  async function submitQuestion() {
    const trimmed = question.trim()
    if (!trimmed || loading) {
      return
    }

    // append 前の完了ペアのみを履歴にする（送信中の質問は含めない）
    const history = buildAskHistory(messages)
    const threadId = appendUserMessage(trimmed)
    setQuestion('')
    await runAsk(threadId, trimmed, history)
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    await submitQuestion()
  }

  function handleQuestionKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return
    }
    if (isComposing || event.nativeEvent.isComposing) {
      return
    }
    event.preventDefault()
    if (loading) {
      return
    }
    void submitQuestion()
  }

  function handleEditMessage(messageId: string) {
    if (!activeThreadId || loading) {
      return
    }
    const content = editUserMessage(activeThreadId, messageId)
    if (content === null) {
      return
    }
    setQuestion(content)
    questionInputRef.current?.focus()
    resizeQuestionInput()
    requestAnimationFrame(() => {
      const el = questionInputRef.current
      if (!el) {
        return
      }
      resizeQuestionInput()
      el.setSelectionRange(el.value.length, el.value.length)
    })
  }

  async function handleRetry() {
    const pending = retryRef.current
    if (!pending || loading) {
      return
    }
    await runAsk(pending.threadId, pending.question, pending.history)
  }

  async function copyTextToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch {
      // fall through to execCommand fallback
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }

  async function handleCopyAnswer(messageId: string, content: string) {
    const ok = await copyTextToClipboard(content)
    if (!ok) {
      setError('クリップボードへのコピーに失敗しました。手動で選択してコピーしてください。')
      return
    }
    setCopiedMessageId(messageId)
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current)
    }
    copyFeedbackTimerRef.current = setTimeout(() => {
      setCopiedMessageId((current) => (current === messageId ? null : current))
      copyFeedbackTimerRef.current = null
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current)
      }
    }
  }, [])

  /** CX13: 回答フィードバック（👍/👎 + 任意コメント）。1 回答 1 回のみ送信できる */
  function getFeedbackState(messageId: string): FeedbackEntryState {
    return feedbackState[messageId] ?? IDLE_FEEDBACK_STATE
  }

  async function submitFeedback(
    messageId: string,
    response: RagAskResponse,
    rating: AnswerFeedbackRating,
    comment: string,
  ) {
    setFeedbackState((prev) => ({ ...prev, [messageId]: { rating, status: 'submitting', comment } }))
    try {
      await submitAnswerFeedback(response.meta.trace_id ?? '', rating, comment)
      setFeedbackState((prev) => ({ ...prev, [messageId]: { rating, status: 'submitted', comment } }))
    } catch {
      setFeedbackState((prev) => ({ ...prev, [messageId]: { rating, status: 'error', comment } }))
    }
  }

  function handleThumbUp(messageId: string, response: RagAskResponse) {
    void submitFeedback(messageId, response, 1, '')
  }

  function handleThumbDown(messageId: string) {
    setFeedbackState((prev) => ({
      ...prev,
      [messageId]: { rating: -1, status: 'commenting', comment: prev[messageId]?.comment ?? '' },
    }))
  }

  function handleFeedbackCommentChange(messageId: string, comment: string) {
    setFeedbackState((prev) => ({
      ...prev,
      [messageId]: { rating: -1, status: 'commenting', comment },
    }))
  }

  function handleFeedbackCommentSubmit(messageId: string, response: RagAskResponse) {
    void submitFeedback(messageId, response, -1, getFeedbackState(messageId).comment)
  }

  usePageHeader('A. AI Q&A', 'AI への質問')

  const activeThreadHidden =
    threads.some((thread) => thread.id === activeThreadId) &&
    !filteredThreads.some((thread) => thread.id === activeThreadId)

  return (
    <div className="page page-wide">
      <p className="lede page-intro">Office 由来ナレッジを根拠に AI が回答します。</p>

      <div className="chat-layout">
        <aside className="chat-history card" aria-label="質問履歴">
          <div className="chat-history-head">
            <h2>質問履歴</h2>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => startNewThread()}
            >
              <IconPlus />
              新しい会話
            </button>
          </div>

          <div className="chat-history-search-row">
            <input
              type="search"
              className="chat-history-search"
              aria-label="履歴を検索"
              placeholder="履歴を検索"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="chat-history-search-clear"
                aria-label="検索条件をクリア"
                onClick={() => setSearchQuery('')}
              >
                <IconX />
              </button>
            )}
          </div>

          {activeThreadHidden && (
            <p className="muted chat-history-active-hidden">
              選択中の会話は検索条件に一致していません。
            </p>
          )}

          {!ready ? (
            <p className="muted chat-history-empty">履歴を読み込み中…</p>
          ) : threads.length === 0 ? (
            <p className="muted chat-history-empty">
              まだ履歴はありません。質問を送信するとここに表示されます。
            </p>
          ) : filteredThreads.length === 0 ? (
            <p className="muted chat-history-empty">該当する会話がありません。</p>
          ) : (
            <ul className="chat-history-list">
              {filteredThreads.map((thread) => {
                const isActive = thread.id === activeThreadId
                return (
                  <li key={thread.id}>
                    <div
                      className={[
                        'chat-history-item',
                        isActive ? 'chat-history-item--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {renameTarget?.id === thread.id ? (
                        <input
                          type="text"
                          className="chat-history-rename-input"
                          autoFocus
                          value={renameTarget.value}
                          onChange={(event) =>
                            setRenameTarget({ id: thread.id, value: event.target.value })
                          }
                          onBlur={() => {
                            renameThread(thread.id, renameTarget.value)
                            setRenameTarget(null)
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              renameThread(thread.id, renameTarget.value)
                              setRenameTarget(null)
                            } else if (event.key === 'Escape') {
                              event.preventDefault()
                              setRenameTarget(null)
                            }
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="chat-history-select"
                          onClick={() => selectThread(thread.id)}
                        >
                          <span className="chat-history-title">{thread.title}</span>
                          <span className="chat-history-meta">
                            {formatThreadDate(thread.updatedAt)}
                            {thread.messages.length > 0 && (
                              <>
                                {' '}
                                ·{' '}
                                {
                                  thread.messages.filter(
                                    (message) => message.role === 'user',
                                  ).length
                                }{' '}
                                件
                              </>
                            )}
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        className="chat-history-rename"
                        aria-label={`「${thread.title}」の名前を変更`}
                        onClick={() => setRenameTarget({ id: thread.id, value: thread.title })}
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="chat-history-delete"
                        aria-label={`「${thread.title}」を削除`}
                        onClick={() => setDeleteTarget({ id: thread.id, title: thread.title })}
                      >
                        <IconX />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </aside>

        <div className="chat-panel card">
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="empty-state">
                <span className="empty-state-icon">
                  <IconChat />
                </span>
                <p>質問を入力して送信してください。履歴はこのブラウザに保存されます。</p>
              </div>
            )}
            {messages.map((message) => {
              const feedback = message.response ? getFeedbackState(message.id) : null
              const response = message.response
              return (
              <div
                key={message.id}
                className={`chat-message-row chat-message-row--${message.role}`}
              >
                <span className="chat-avatar" aria-hidden="true">
                  {message.role === 'user' ? 'You' : 'AI'}
                </span>
                <article className={`chat-message chat-message-${message.role}`}>
                  {message.role === 'assistant' ? (
                    <div className="chat-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="chat-content">{message.content}</p>
                  )}
                  {message.role === 'assistant' && (
                    <div className="chat-message-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void handleCopyAnswer(message.id, message.content)}
                        aria-label="回答をコピー"
                      >
                        {copiedMessageId === message.id ? <IconCheck /> : <IconCopy />}
                        {copiedMessageId === message.id ? 'コピーしました' : 'コピー'}
                      </button>
                      {response && feedback && feedback.status === 'submitted' && (
                        <span className="feedback-submitted" role="status">
                          <IconCheck />
                          フィードバックを送信しました
                        </span>
                      )}
                      {response && feedback && feedback.status !== 'submitted' && (
                        <>
                          <button
                            type="button"
                            className={`btn btn-ghost btn-sm feedback-btn${feedback.rating === 1 ? ' is-selected' : ''}`}
                            onClick={() => handleThumbUp(message.id, response)}
                            disabled={feedback.status === 'submitting' || feedback.status === 'commenting'}
                            aria-label="この回答は役に立った"
                          >
                            <IconThumbsUp />
                            役に立った
                          </button>
                          <button
                            type="button"
                            className={`btn btn-ghost btn-sm feedback-btn${feedback.rating === -1 ? ' is-selected' : ''}`}
                            onClick={() => handleThumbDown(message.id)}
                            disabled={feedback.status === 'submitting' || feedback.status === 'commenting'}
                            aria-label="この回答は役に立たなかった"
                          >
                            <IconThumbsDown />
                            役に立たなかった
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {message.role === 'user' && (
                    <div className="chat-message-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={loading}
                        onClick={() => handleEditMessage(message.id)}
                        aria-label="質問を編集して再送信（この回答は削除されます）"
                        title="質問を編集して再送信（この回答は削除されます）"
                      >
                        <IconPencil />
                        編集して再送信
                      </button>
                    </div>
                  )}
                  {response && feedback && feedback.status === 'commenting' && (
                    <div className="feedback-comment">
                      <textarea
                        value={feedback.comment}
                        onChange={(e) => handleFeedbackCommentChange(message.id, e.target.value)}
                        placeholder="コメント（任意）"
                        rows={2}
                        maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleFeedbackCommentSubmit(message.id, response)}
                      >
                        送信
                      </button>
                    </div>
                  )}
                  {response && feedback && feedback.status === 'error' && (
                    <p className="feedback-error">フィードバックの送信に失敗しました。もう一度お試しください。</p>
                  )}
                  {message.response && message.response.citations.length > 0 && (
                    <div className="citations">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm citations-reveal"
                        aria-expanded={visibleCitations.has(message.id)}
                        onClick={() => toggleCitationsVisible(message.id)}
                      >
                        {visibleCitations.has(message.id)
                          ? '根拠ナレッジを隠す'
                          : `根拠ナレッジを表示（${message.response.citations.length}件）`}
                      </button>
                      {visibleCitations.has(message.id) && (
                        <>
                          <ul className="citation-list">
                            {(expandedCitations.has(message.id)
                              ? message.response.citations
                              : message.response.citations.slice(0, CITATIONS_COLLAPSED_LIMIT)
                            ).map((citation) => {
                              const heading = displayDocumentTitle(
                                citation.title,
                                citation.source_uri,
                                citation.document_id,
                              )
                              const label = sourceUriLabel(citation.source_uri)
                              const typeLabel = sourceTypeLabel(citation.source_uri)
                              const officeUrl = extractReferenceUrl(citation.snippet)
                              const hasDocument = Boolean(citation.document_id?.trim())
                              return (
                                <li
                                  key={citation.chunk_id || citation.document_id}
                                  className="citation-item"
                                >
                                  <span className="citation-icon">
                                    <IconLink />
                                  </span>
                                  <span className="citation-body">
                                    {hasDocument ? (
                                      <Link
                                        to={`/rag/documents/${citation.document_id}`}
                                        className="citation-link"
                                      >
                                        {heading}
                                      </Link>
                                    ) : (
                                      <span className="citation-link">{heading}</span>
                                    )}
                                    <p className="citation-meta">
                                      <span className="type-badge">{typeLabel}</span>
                                      <span className="citation-source">{label}</span>
                                    </p>
                                    {citation.snippet && (
                                      <p className="citation-snippet">{citation.snippet}</p>
                                    )}
                                    <span className="citation-actions">
                                      {hasDocument && (
                                        <Link
                                          to={`/rag/documents/${citation.document_id}`}
                                          className="btn btn-ghost btn-sm"
                                        >
                                          詳細
                                        </Link>
                                      )}
                                      {officeUrl && (
                                        <a
                                          href={officeUrl}
                                          className="btn btn-ghost btn-sm"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Office で開く
                                        </a>
                                      )}
                                      {canCurate && hasDocument && (
                                        <button
                                          type="button"
                                          className="btn btn-ghost btn-sm"
                                          onClick={() =>
                                            setKnowledgeDeleteTarget({
                                              document_id: citation.document_id,
                                              title: heading,
                                            })
                                          }
                                        >
                                          <IconTrash />
                                          削除申請
                                        </button>
                                      )}
                                    </span>
                                  </span>
                                </li>
                              )
                            })}
                          </ul>
                          {message.response.citations.length > CITATIONS_COLLAPSED_LIMIT && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm citations-toggle"
                              onClick={() => toggleCitationsExpanded(message.id)}
                            >
                              {expandedCitations.has(message.id)
                                ? '折りたたむ'
                                : `もっと見る（全 ${message.response.citations.length} 件）`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </article>
              </div>
              )
            })}
            {loading && (
              <div className="chat-message-row chat-message-row--assistant">
                <span className="chat-avatar" aria-hidden="true">
                  AI
                </span>
                <article className="chat-message chat-message-assistant">
                  <p className="chat-content muted">
                    回答を生成中…（最大 {Math.round(ASK_TIMEOUT_MS / 1000)} 秒）
                  </p>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleCancel}
                  >
                    キャンセル
                  </button>
                </article>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {error && (
            <div className="chat-error-bar" role="alert">
              <p className="form-error">{error}</p>
              {canRetry && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={loading}
                  onClick={() => void handleRetry()}
                >
                  再試行
                </button>
              )}
            </div>
          )}
          <form className="chat-form" onSubmit={(event) => void handleSubmit(event)}>
            <label className="field">
              <span className="sr-only">質問</span>
              <textarea
                ref={questionInputRef}
                rows={1}
                placeholder="例: 備品購入の申請フローを教えて"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={handleQuestionKeyDown}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                disabled={loading}
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary chat-send"
              disabled={loading}
              aria-label="質問する"
            >
              <IconSend />
            </button>
          </form>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDialog
          variant="reject"
          title="スレッドの削除"
          message={<>「{deleteTarget.title}」を削除しますか？この操作は取り消せません。</>}
          confirmLabel="削除する"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            deleteThread(deleteTarget.id)
            setDeleteTarget(null)
          }}
        />
      )}

      {knowledgeDeleteTarget && (
        <DeleteRequestDialog
          document={knowledgeDeleteTarget}
          onClose={() => setKnowledgeDeleteTarget(null)}
          onSuccess={() => setKnowledgeDeleteTarget(null)}
        />
      )}
    </div>
  )
}
